import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UploadCloud, FileJson } from "lucide-react";
import { importProducts, formatApiErrorDetail } from "@/admin/adminApi";

/** Loads a JSON file (prepared by the AI builder — scraped photos, specs,
 copy) and bulk creates/updates products in whichever environment this admin
 is logged into. This is how a product built in preview gets published to
 production: choose the same file here again once logged into the live
 admin — no manual field-by-field re-entry required. */
export const ImportProductsDialog = ({ open, onOpenChange, category, onImported }) => {
  const [raw, setRaw] = useState("");
  const [fileName, setFileName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError(null);
    const reader = new FileReader();
    reader.onload = () => setRaw(reader.result);
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setError(null);
    setResult(null);
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      setError("That's not valid JSON — check for a missing comma or bracket.");
      return;
    }
    const products = Array.isArray(parsed) ? parsed : parsed.products;
    if (!Array.isArray(products) || products.length === 0) {
      setError("Expected a JSON array of products (or an object with a \"products\" array).");
      return;
    }
    setBusy(true);
    try {
      const data = await importProducts(category, products);
      setResult(data);
      if (data.errors.length === 0) onImported();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[var(--bg-secondary)] border-[var(--border-default)] text-white">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <UploadCloud size={18} className="text-[var(--brand-primary)]" /> Import Products
          </DialogTitle>
        </DialogHeader>
        <p className="text-white/60 text-sm">
          Choose the JSON file provided by the AI builder. Existing products (matched by slug) are updated in place; new ones are added to the end of the list — drag to reorder afterward.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          onChange={handleFileChosen}
          className="hidden"
          data-testid="import-products-file-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          data-testid="import-products-choose-file-button"
          className="w-full flex items-center justify-center gap-2 border border-dashed border-[var(--border-default)] hover:border-[var(--brand-primary)] px-4 py-6 text-white/70 hover:text-white transition-colors"
        >
          <FileJson size={18} />
          {fileName ? fileName : "Choose JSON file…"}
        </button>
        <details className="text-white/50 text-xs">
          <summary className="cursor-pointer select-none">Or paste the JSON directly</summary>
          <textarea
            data-testid="import-products-textarea"
            value={raw}
            onChange={(e) => { setRaw(e.target.value); setFileName(null); }}
            placeholder='[{ "slug": "...", "name": "...", "gallery": [...] }]'
            rows={10}
            className="mt-2 w-full bg-[var(--bg-main)] border border-[var(--border-default)] px-3 py-2 text-white text-xs font-mono focus:border-[var(--brand-primary)] outline-none"
          />
        </details>
        {error && <p data-testid="import-products-error" className="text-red-400 text-sm">{error}</p>}
        {result && (
          <p data-testid="import-products-result" className="text-sm">
            <span className="text-[var(--brand-primary)]">{result.created.length} created</span>
            {", "}
            <span className="text-white/70">{result.updated.length} updated</span>
            {result.errors.length > 0 && (
              <span className="text-red-400"> — {result.errors.length} failed: {result.errors.map((e) => `${e.slug} (${e.error})`).join("; ")}</span>
            )}
          </p>
        )}
        <DialogFooter>
          <button
            type="button"
            data-testid="import-products-submit"
            onClick={handleImport}
            disabled={busy || !raw.trim()}
            className="btn-primary disabled:opacity-50"
          >
            {busy ? "Importing…" : "Import"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
